import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  FormSection,
  Input,
  PageShell,
  SectionCard,
  StickyFooter,
  toast,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { friendlyMessage } from '@/lib/errors';
import { RadioRow } from '@/features/shippingFee/components/RadioRow';
import { MemberSelector } from '../components/MemberSelector';
import { ProductSelectModal } from '../components/ProductSelectModal';
import { ManualOrderResultModal } from '../components/ManualOrderResultModal';
import { useManualOrderCreateMutation } from '../hooks/useManualOrder';
import { MANUAL_ORDER_WRITE_ROLES } from '../lib/manualOrderRbac';
import {
  computeItemAmount,
  computePayable,
  parseAmount,
  rowUnitPrice,
  toRequestLines,
} from '../lib/manualOrderCalc';
import {
  FULFILLMENT_TYPES,
  MANUAL_PAYMENT_METHODS,
  type FulfillmentType,
  type ManualOrderCreateRequest,
  type ManualOrderCreateResponse,
  type ManualPaymentMethod,
  type MemberType,
  type SelectedProductRow,
} from '../types/manualOrder';

const MEMBER_TYPES: ReadonlyArray<MemberType> = ['MEMBER', 'GUEST'];

const won = (n: number): string => `${n.toLocaleString('ko-KR')}원`;

export const ManualOrderPage = () => {
  const { t } = useTranslation('manualOrder');
  const navigate = useNavigate();
  const canWrite = useHasRole(MANUAL_ORDER_WRITE_ROLES);
  const create = useManualOrderCreateMutation();

  const memberTypeLabels: Record<MemberType, string> = {
    MEMBER: t('manualOrder.memberType.MEMBER'),
    GUEST: t('manualOrder.memberType.GUEST'),
  };
  const fulfillmentTypeLabels: Record<FulfillmentType, string> = {
    DELIVERY: t('manualOrder.fulfillmentType.DELIVERY'),
    PICKUP: t('manualOrder.fulfillmentType.PICKUP'),
  };
  const paymentMethodLabels: Record<ManualPaymentMethod, string> = {
    BANK_TRANSFER: t('manualOrder.paymentMethod.BANK_TRANSFER'),
    PAID: t('manualOrder.paymentMethod.PAID'),
  };

  // 주문자
  const [memberType, setMemberType] = useState<MemberType>('MEMBER');
  const [userId, setUserId] = useState<string | null>(null);
  const [ordererName, setOrdererName] = useState('');
  const [ordererPhone, setOrdererPhone] = useState('');
  const [ordererEmail, setOrdererEmail] = useState('');

  // 상품
  const [products, setProducts] = useState<SelectedProductRow[]>([]);
  const [productModalOpen, setProductModalOpen] = useState(false);

  // 배송/수령자
  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentType>('DELIVERY');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientPostalCode, setRecipientPostalCode] = useState('');
  const [recipientAddress1, setRecipientAddress1] = useState('');
  const [recipientAddress2, setRecipientAddress2] = useState('');
  const [recipientCountryCode, setRecipientCountryCode] = useState('KR');
  const [deliveryMessage, setDeliveryMessage] = useState('');

  // 결제
  const [shippingFee, setShippingFee] = useState('0');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState<ManualPaymentMethod>('BANK_TRANSFER');
  const [paymentDueAt, setPaymentDueAt] = useState('');

  const [adminMemo, setAdminMemo] = useState('');

  const [result, setResult] = useState<ManualOrderCreateResponse | null>(null);

  const itemAmount = useMemo(() => computeItemAmount(products), [products]);
  const shippingFeeNum = parseAmount(shippingFee);
  const discountNum = parseAmount(discountAmount);
  const payable = computePayable(itemAmount, discountNum, shippingFeeNum);

  const isDelivery = fulfillmentType === 'DELIVERY';

  const validate = (): string | null => {
    if (memberType === 'MEMBER' && !userId) return t('manualOrder.validation.memberRequired');
    if (memberType === 'GUEST') {
      if (ordererName.trim() === '') return t('manualOrder.validation.guestNameRequired');
      if (ordererPhone.trim() === '') return t('manualOrder.validation.guestPhoneRequired');
    }
    if (products.length === 0) return t('manualOrder.validation.productsRequired');
    if (discountNum > itemAmount + shippingFeeNum) {
      return t('manualOrder.validation.discountExceeds');
    }
    if (isDelivery && recipientName.trim() === '') {
      return t('manualOrder.validation.recipientRequired');
    }
    return null;
  };

  const buildRequest = (): ManualOrderCreateRequest => {
    const req: ManualOrderCreateRequest = {
      memberType,
      lines: toRequestLines(products),
      fulfillmentType,
      shippingFee: shippingFeeNum,
      discountAmount: discountNum,
      paymentMethod,
    };
    if (memberType === 'MEMBER') {
      req.userId = userId;
    } else {
      req.ordererName = ordererName.trim();
      req.ordererPhone = ordererPhone.trim();
      if (ordererEmail.trim()) req.ordererEmail = ordererEmail.trim();
    }
    if (paymentMethod === 'BANK_TRANSFER' && paymentDueAt) {
      // datetime-local → ISO-8601(UTC). 미입력 시 서버 기본 TTL.
      req.paymentDueAt = new Date(paymentDueAt).toISOString();
    }
    if (isDelivery) {
      if (recipientName.trim()) req.recipientName = recipientName.trim();
      if (recipientPhone.trim()) req.recipientPhone = recipientPhone.trim();
      if (recipientPostalCode.trim()) req.recipientPostalCode = recipientPostalCode.trim();
      if (recipientAddress1.trim()) req.recipientAddress1 = recipientAddress1.trim();
      if (recipientAddress2.trim()) req.recipientAddress2 = recipientAddress2.trim();
      if (recipientCountryCode.trim()) req.recipientCountryCode = recipientCountryCode.trim();
      if (deliveryMessage.trim()) req.deliveryMessage = deliveryMessage.trim();
    }
    if (adminMemo.trim()) req.adminMemo = adminMemo.trim();
    return req;
  };

  const handleSubmit = () => {
    if (create.isPending) return;
    const message = validate();
    if (message) {
      toast.error(message);
      return;
    }
    create.mutate(buildRequest(), {
      onSuccess: (data) => {
        toast.success(t('manualOrder.toast.success'));
        setResult(data);
      },
      onError: (err) => toast.error(friendlyMessage(err, t('manualOrder.toast.error'))),
    });
  };

  const removeProduct = (productId: string) => {
    setProducts((prev) => prev.filter((p) => p.productId !== productId));
  };

  return (
    <PageShell
      title={t('manualOrder.title')}
      breadcrumb={[
        { label: t('manualOrder.breadcrumb.orders') },
        { label: t('manualOrder.breadcrumb.manual') },
      ]}
      description={t('manualOrder.description')}
      data-testid="manual-order-page"
    >
      {/* 주문자 정보 */}
      <SectionCard title={t('manualOrder.orderer.section')}>
        <RadioRow
          name="mo-member-type"
          label={t('manualOrder.orderer.memberType')}
          options={MEMBER_TYPES}
          labels={memberTypeLabels}
          value={memberType}
          onChange={setMemberType}
          disabled={!canWrite || create.isPending}
        />
        {memberType === 'MEMBER' ? (
          <FormSection label={t('manualOrder.orderer.memberSearch')} density="compact" required>
            <MemberSelector
              value={userId}
              disabled={!canWrite || create.isPending}
              onChange={setUserId}
            />
          </FormSection>
        ) : (
          <>
            <FormSection label={t('manualOrder.orderer.name')} density="compact" required>
              <Input
                value={ordererName}
                onChange={(e) => setOrdererName(e.target.value)}
                disabled={!canWrite || create.isPending}
                className="max-w-[280px]"
                data-testid="manual-order-orderer-name"
              />
            </FormSection>
            <FormSection label={t('manualOrder.orderer.phone')} density="compact" required>
              <Input
                value={ordererPhone}
                onChange={(e) => setOrdererPhone(e.target.value)}
                placeholder={t('manualOrder.recipient.phonePlaceholder')}
                disabled={!canWrite || create.isPending}
                className="max-w-[280px]"
                data-testid="manual-order-orderer-phone"
              />
            </FormSection>
            <FormSection label={t('manualOrder.orderer.email')} density="compact">
              <Input
                type="email"
                value={ordererEmail}
                onChange={(e) => setOrdererEmail(e.target.value)}
                disabled={!canWrite || create.isPending}
                className="max-w-[280px]"
              />
            </FormSection>
          </>
        )}
      </SectionCard>

      {/* 주문 상품 */}
      <SectionCard
        title={t('manualOrder.products.section')}
        aside={
          <Button
            size="sm"
            variant="outline"
            onClick={() => setProductModalOpen(true)}
            disabled={!canWrite || create.isPending}
            data-testid="manual-order-open-product-modal"
          >
            <Pencil size={14} aria-hidden="true" />
            {t('manualOrder.products.select')}
          </Button>
        }
      >
        {products.length === 0 ? (
          <p className="py-6 text-center text-[13px]" style={{ color: 'var(--content-muted)' }}>
            {t('manualOrder.products.empty')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-md" style={{ border: '1px solid var(--divider-soft)' }}>
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr style={{ background: 'var(--surface-raised)' }}>
                  <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>{t('manualOrder.products.colName')}</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--content-muted)' }}>{t('manualOrder.products.colQty')}</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--content-muted)' }}>{t('manualOrder.products.colUnitPrice')}</th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--content-muted)' }}>{t('manualOrder.products.colAmount')}</th>
                  <th className="w-[44px] px-2 py-2" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const unit = rowUnitPrice(p);
                  return (
                    <tr key={p.productId} style={{ borderTop: '1px solid var(--divider-soft)' }}>
                      <td className="px-3 py-2" style={{ color: 'var(--content-strong)' }}>{p.productName}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.qty}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {parseAmount(p.unitPrice) > 0 ? won(unit) : t('manualOrder.products.unitAuto', { price: won(unit) })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--content-strong)' }}>
                        {won(unit * p.qty)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeProduct(p.productId)}
                          aria-label={t('manualOrder.products.remove')}
                          style={{ color: 'var(--signal-danger)', cursor: 'pointer' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* 수령자 정보 (배송 시) */}
      <SectionCard title={t('manualOrder.recipient.section')}>
        <RadioRow
          name="mo-fulfillment"
          label={t('manualOrder.recipient.fulfillmentType')}
          options={FULFILLMENT_TYPES}
          labels={fulfillmentTypeLabels}
          value={fulfillmentType}
          onChange={setFulfillmentType}
          disabled={!canWrite || create.isPending}
        />
        {isDelivery ? (
          <>
            <FormSection label={t('manualOrder.recipient.name')} density="compact" required>
              <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} disabled={!canWrite || create.isPending} className="max-w-[280px]" data-testid="manual-order-recipient-name" />
            </FormSection>
            <FormSection label={t('manualOrder.recipient.phone')} density="compact">
              <Input value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder={t('manualOrder.recipient.phonePlaceholder')} disabled={!canWrite || create.isPending} className="max-w-[280px]" />
            </FormSection>
            <FormSection label={t('manualOrder.recipient.postalCode')} density="compact">
              <Input value={recipientPostalCode} onChange={(e) => setRecipientPostalCode(e.target.value)} disabled={!canWrite || create.isPending} className="max-w-[160px] tabular-nums" />
            </FormSection>
            <FormSection label={t('manualOrder.recipient.address1')} density="compact">
              <Input value={recipientAddress1} onChange={(e) => setRecipientAddress1(e.target.value)} disabled={!canWrite || create.isPending} />
            </FormSection>
            <FormSection label={t('manualOrder.recipient.address2')} density="compact">
              <Input value={recipientAddress2} onChange={(e) => setRecipientAddress2(e.target.value)} disabled={!canWrite || create.isPending} />
            </FormSection>
            <FormSection label={t('manualOrder.recipient.countryCode')} density="compact">
              <Input value={recipientCountryCode} onChange={(e) => setRecipientCountryCode(e.target.value)} placeholder="KR" maxLength={2} disabled={!canWrite || create.isPending} className="max-w-[100px]" />
            </FormSection>
            <FormSection label={t('manualOrder.recipient.deliveryMessage')} density="compact">
              <Input value={deliveryMessage} onChange={(e) => setDeliveryMessage(e.target.value)} placeholder={t('manualOrder.recipient.deliveryMessagePlaceholder')} disabled={!canWrite || create.isPending} />
            </FormSection>
          </>
        ) : (
          <p className="px-1 py-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {t('manualOrder.recipient.pickupNotice')}
          </p>
        )}
      </SectionCard>

      {/* 결제 정보 */}
      <SectionCard title={t('manualOrder.payment.section')}>
        <div className="flex flex-col gap-2 py-1">
          <div className="flex items-center justify-between text-[13px]">
            <span style={{ color: 'var(--content-muted)' }}>{t('manualOrder.payment.itemAmount')}</span>
            <span className="tabular-nums" style={{ color: 'var(--content-strong)' }} data-testid="manual-order-item-amount">{won(itemAmount)}</span>
          </div>
          <FormSection label={t('manualOrder.payment.shippingFee')} density="compact">
            <Input type="number" min={0} step="1" value={shippingFee} onChange={(e) => setShippingFee(e.target.value)} disabled={!canWrite || create.isPending} className="max-w-[200px] tabular-nums" data-testid="manual-order-shipping-fee" />
          </FormSection>
          <FormSection label={t('manualOrder.payment.discount')} density="compact">
            <Input type="number" min={0} step="1" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} disabled={!canWrite || create.isPending} className="max-w-[200px] tabular-nums" data-testid="manual-order-discount" />
          </FormSection>
          <div className="mt-1 flex items-center justify-between border-t pt-2 text-[14px]" style={{ borderColor: 'var(--divider-soft)' }}>
            <span className="font-semibold" style={{ color: 'var(--content-strong)' }}>{t('manualOrder.payment.payable')}</span>
            <span className="tabular-nums font-bold" style={{ color: 'var(--content-strong)' }} data-testid="manual-order-payable">{won(payable)}</span>
          </div>
        </div>
      </SectionCard>

      {/* 결제 수단 */}
      <SectionCard title={t('manualOrder.method.section')}>
        <RadioRow
          name="mo-payment-method"
          label={t('manualOrder.method.label')}
          options={MANUAL_PAYMENT_METHODS}
          labels={paymentMethodLabels}
          value={paymentMethod}
          onChange={setPaymentMethod}
          disabled={!canWrite || create.isPending}
        />
        {paymentMethod === 'BANK_TRANSFER' ? (
          <FormSection label={t('manualOrder.method.dueAt')} density="compact">
            <Input
              type="datetime-local"
              value={paymentDueAt}
              onChange={(e) => setPaymentDueAt(e.target.value)}
              disabled={!canWrite || create.isPending}
              className="max-w-[260px]"
            />
          </FormSection>
        ) : (
          <p className="px-1 py-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {t('manualOrder.method.paidNotice')}
          </p>
        )}
      </SectionCard>

      {/* 관리자 메모 */}
      <SectionCard title={t('manualOrder.memo.section')}>
        <textarea
          value={adminMemo}
          onChange={(e) => setAdminMemo(e.target.value)}
          rows={3}
          placeholder={t('manualOrder.memo.placeholder')}
          disabled={!canWrite || create.isPending}
          className="w-full rounded-md px-3 py-2 text-[13px] outline-none focus:[box-shadow:var(--focus-ring-brand)]"
          style={{
            background: 'var(--surface-0)',
            border: '1px solid var(--divider-strong)',
            color: 'var(--content-default)',
          }}
          data-testid="manual-order-memo"
        />
      </SectionCard>

      <StickyFooter>
        <Button type="button" variant="outline" onClick={() => navigate('/orders')} disabled={create.isPending}>
          {t('manualOrder.footer.cancel')}
        </Button>
        <Button type="button" variant="cta" onClick={handleSubmit} disabled={!canWrite || create.isPending} data-testid="manual-order-submit">
          {create.isPending ? t('manualOrder.footer.submitting') : t('manualOrder.footer.submit')}
        </Button>
      </StickyFooter>

      <ProductSelectModal
        open={productModalOpen}
        onOpenChange={setProductModalOpen}
        initialSelection={products}
        onApply={setProducts}
      />

      <ManualOrderResultModal
        result={result}
        onClose={() => {
          setResult(null);
          navigate('/orders');
        }}
      />
    </PageShell>
  );
};
